import Plugin from './plugin.js'

/**
 * Exception if an invalid plugin definition was found
 */
class InvalidPluginDefintionException {
  constructor (message) {
    this.message = message
  }
}

/**
 * scanner to read plugin definitions
 * @thanks to http://sstephenson.github.io/strscan-js/
 */
class StringScanner {
  constructor (source) {
    this.source = source
    this.reset()
  }

  scan (regexp) {
    const matches = regexp.exec(this.getRemainder())
    return matches && matches.index === 0 ? this.setState(matches, {
      head: this.head + matches[0].length,
      last: this.head
    }) : this.setState([])
  }

  check (regexp) {
    const matches = regexp.exec(this.getRemainder())
    return matches && matches.index === 0 ? this.setState(matches) : this.setState([])
  }

  getRemainder () {
    return this.source.slice(this.head)
  }

  hasTerminated () {
    return this.head === this.source.length
  }

  reset () {
    return this.setState([], {
      head: 0,
      last: 0
    })
  }

  setState (matches, values) {
    this.head = this._calcStatePos(values, 'head')
    this.last = this._calcStatePos(values, 'last')
    this.captures = matches.slice(1)
    return (this.match = matches[0])
  }

  _calcStatePos (values, posType) {
    let a
    return (typeof (a = ((typeof values === 'undefined' || values === null) ? undefined : values[posType])) !== 'undefined' && a !== null) ? a : this[posType]
  }
}

/**
 * Parser to find elements and read attributes
 */
export default class {
  constructor () {
    this.PLUGIN_AND = /,/
    this.PLUGIN_PIPE = /\|/
    this.PLUGIN_GROUP_OPEN = /[(]/
    this.PLUGIN_GROUP_CLOSE = /[)]/
    this.PLUGIN_NAME = /[A-Za-z]+/
  }

  _c (str, chars, initial, op) {
    return chars.reduce((accumulator, currentValue) => {
      return op(accumulator, str.indexOf(currentValue) > -1)
    }, initial)
  }

  /**
   * checks if the given string contains one of the given charachters
   * @param str
   * @param chars
   * @return true if the string contains one of the given charachter
   */
  _contains (str, ...chars) {
    return this._c(str, chars, false, (a, b) => a || b)
  }

  /**
   * checks if the given string contains all of the given charachters
   * @param str
   * @param chars
   * @return true if the string contains all of the given charachter
   */
  _containsAll (str, ...chars) {
    return this._c(str, chars, true, (a, b) => a && b)
  }

  /**
   * creates an object with the given key and value
   * @param key
   * @param value
   */
  _createObject (key, value) {
    return {
      [key]: value
    }
  }

  _modifyDataOptionKey (key, cutUntil) {
    const tmp = key.substring(cutUntil)
    return tmp.charAt(0).toLowerCase() + tmp.substring(1)
  }

  /**
   * reads an attribute value and return the trimmed value
   * @param element to read the attribute from
   * @param attrName name to read
   * @return trimmed value
   */
  _readDataAttr (element, attrName) {
    const attrValue = element.dataset[attrName]
    return attrValue ? attrValue.trim() : null
  }

  /**
   * find the first element, that match the given selector.
   * @param selector
   * @return element
   */
  find (selector) {
    return document.body.querySelector(selector)
  }

  /**
   * find all elements, that match the given selector.
   * @param selector
   * @return NodeList of elements (https://developer.mozilla.org/de/docs/Web/API/NodeList)
   */
  findAll (selector) {
    return this.findAllBelow(document.body, selector)
  }

  /**
   * find all elements, that match the given selector.
   * @param element search for elements below this one
   * @param selector selector to search for
   * @return NodeList of elements (https://developer.mozilla.org/de/docs/Web/API/NodeList)
   */
  findAllBelow (element, selector) {
    return element.querySelectorAll(selector)
  }

  /**
   * find the script element it was loaded from. If document#currentScript
   * is not supported, the given selector will be used to find the right element.
   * @param selector
   * @return element
   */
  findSelf (selector) {
    const element = document.currentScript
    return element || this.find(selector)
  }

  /**
   * read options from data attributes, that must be defined on the given Element.
   *
   * e.g.: To read the Attribute <code>data-ns-opt="foo"</code>,
   * this method should be called <code>Parser#readOptions(element, 'ns', { opt: 'bar' });</code>
   * If the attribute will be removed, the default value 'bar' will be returned
   *
   *
   *
   * @param element The element with data- Atributes to define options
   * @param namespace The namespace will be the prefix for every Option, defined in the given options
   * @param options only the Options in this object will be read. If no default value is needed,
   *                just define it as <code>null</code>
   */
  readOptions (element, namespace, options) {
    return Object.assign({}, options, element ? Object.keys(element.dataset)
                // filter data attributes by the given namespace
                .filter((k) => k.indexOf(namespace) === 0)
                // map attr key to a Key-Value-Object
                .map((k) => this._createObject(
                  this._modifyDataOptionKey(k, namespace.length),
                  element.dataset[k]))
                // reduce the single Key-Value-Objects to one big options Object
                .reduce((a, b) => Object.assign(a, b), {}) : {})
  }

  /**
   * reads a single plugin definition
   * @param element
   * @param namespace
   * @return Plugin or null
   */
  readPlugin (element, namespace) {
    let pluginDef = this._readDataAttr(element, namespace + 'Plugin')

    // is something defined? if not return
    if (!pluginDef) return null

    // make sure it is a single plugin definition
    if (this._contains(pluginDef, '|', ',', ' ')) {
      throw new InvalidPluginDefintionException('Looks like you try to excute multiple plugins. Use the plugins Attribute and not plugin. Please have a look at the documentation for details: https://github.com/alexanderbartels/jet/tree/master#how-to-execute-a-plugin')
    }

    // return plugin
    return new Plugin(pluginDef)
  }

  /**
   * reads multiple plugin definitions
   * @param element
   * @param namespace
   * @return Array of Plugins or null
   */
  readPlugins (element, namespace) {
    const pluginDefs = this._readDataAttr(element, namespace + 'Plugins')

    // is something defined? if not return
    if (!pluginDefs) return null

    const scanner = new StringScanner(pluginDefs.replace(/[\s]+/g, ''))
    const plugins = []
    let p

    do {
      if (plugins.length >= 1) {
        // and
        if (scanner.check(/,[A-Za-z]+/)) {
          scanner.scan(this.PLUGIN_AND)
          p = scanner.scan(this.PLUGIN_NAME)
          plugins.push(new Plugin(p))

        // single pipe
        } else if (!this._hasLastPipedPlugins(plugins) && scanner.check(/[|][A-Za-z]+/)) {
          scanner.scan(this.PLUGIN_PIPE)
          let pipedPlugin = new Plugin(scanner.scan(this.PLUGIN_NAME))
          plugins[plugins.length - 1].addPipedPlugin(pipedPlugin)

          // more piped plugins to add?
          this._nextPlugins(scanner, this.PLUGIN_PIPE, (pname) => {
            let temp = new Plugin(pname)
            pipedPlugin.addPipedPlugin(temp)
            pipedPlugin = temp
          })

        // group of plugins after pipe
        } else if (!this._hasLastPipedPlugins(plugins) && scanner.check(/[|][(][A-Za-z]+/)) {
          scanner.scan(this.PLUGIN_PIPE)
          scanner.scan(this.PLUGIN_GROUP_OPEN)

          const plugin = plugins[plugins.length - 1]
          plugin.addPipedPlugin(new Plugin(scanner.scan(this.PLUGIN_NAME)))

          // add remaining plugins from group (, => PLUGIN_AND)
          this._nextPlugins(scanner, this.PLUGIN_AND, (pname) => {
            plugin.addPipedPlugin(new Plugin(pname))
          })

          // close group
          if (!scanner.scan(this.PLUGIN_GROUP_CLOSE)) {
            throw new InvalidPluginDefintionException(`Invalid Plugin definition! Expected group close symbol ) at position ${scanner.head}`)
          }
        } else {
          throw new InvalidPluginDefintionException(scanner.source + ' - ' + scanner.head + 'Uhah!!! Invalid Plugin definition. ' + plugins.length)
        }
      } else if ((p = scanner.scan(this.PLUGIN_NAME))) {
        plugins.push(new Plugin(p))
      } else {
        // invalid plugin definition
        throw new InvalidPluginDefintionException('Invalid Plugin definition: ' + scanner.source + 'at position' + scanner.head)
      }
    } while (!scanner.hasTerminated())

    return plugins
  }

  /**
   * checks if the last plugin in the given array contains pipedPlugins
   * @param plugins array of plugins
   */
  _hasLastPipedPlugins (plugins) {
    return plugins[plugins.length - 1].pipedPlugins.length > 0
  }

  _nextPlugins (scanner, regexp, handler) {
    let p
    while (!scanner.hasTerminated() &&
          scanner.scan(regexp) &&
          (p = scanner.scan(this.PLUGIN_NAME))) {
      handler(p)
    }
  }
}
